import {
  prisma,
  MeetingType,
  NetworkingBehavior,
  NetworkingType,
  MeetingStatus,
  ParticipantType,
  NotificationsType,
  NotificationEntities,
  MeetingParticipantType,
} from "client";
import { createNotification, updateNotification } from "services/notification";
import { sendExpoNotification } from "@sajilni/utils/expoNotification";
import { getDeviceTokensByUser } from "services/deviceToken";
export const checkEntityAvailability = async (
  entityId: string,
  startDate: Date,
  endDate: Date,
  isReceiver: boolean = false
): Promise<boolean> => {
  const conflictingMeetings = await prisma.meeting.findMany({
    where: {
      AND: [
        {
          startDate: { lt: endDate },
          endDate: { gt: startDate },
        },
        { status: { not: MeetingStatus.REJECTED } },
      ],
      participants: {
        some: {
          entityId: entityId,
        },
      },
    },
  });
  // If there are any conflicting meetings, return false (indicating the sender is not available)
  if (conflictingMeetings.length > 0) {
    return false;
  }

  // No conflicts found
  return true;
};

export const checkAvailabilityAndCreateMeetingRequest = async ({
  senderId,
  receiverId,
  startDate,
  endDate,
  eventId,
  receiverType,
  senderType,
  lang = "en",
  roomId,
  location,
}: {
  senderId: string;
  receiverId: string;
  startDate: Date;
  endDate: Date;
  eventId: string;
  receiverType: ParticipantType;
  senderType: ParticipantType;
  lang?: string;
  roomId?: string;
  location?: string;
}) => {
  try {
    if (endDate <= startDate) {
      throw new Error("End date must be after the start date.");
    }

    // Check sender's availability (just check that the sender does not have a conflicting meeting)
    const isSenderAvailable = await checkEntityAvailability(
      senderId,
      startDate,
      endDate,
      false
    );
    if (!isSenderAvailable) {
      throw new Error(
        "The sender has a conflicting meeting during the requested time."
      );
    }
    if (roomId) {
      const isRoomExist = await prisma.meetingRoom.findUnique({
        where: { id: roomId },
      });
      if (!isRoomExist) {
        throw new Error("The room does not exist.");
      }
    }

    // Check receiver's availability (check that the receiver has no conflicting meetings)
    const isReceiverAvailable = await checkEntityAvailability(
      receiverId,
      startDate,
      endDate,
      true
    );
    if (!isReceiverAvailable) {
      throw new Error(
        "The receiver has a conflicting meeting during the requested time."
      );
    }
    const checkReceiverNetworkSetting = await prisma.networking.findFirst({
      where: {
        eventId,
        entityId: receiverId,
        behavior: NetworkingBehavior.ALL,
      },
    });
    let meetingStatus: MeetingStatus = MeetingStatus.ACCEPTED;
    if (!checkReceiverNetworkSetting) {
      throw new Error("The receiver has no networking setting.");
    }
    if (checkReceiverNetworkSetting.type !== NetworkingType.PUBLIC) {
      meetingStatus = MeetingStatus.PENDING;
    }

    // Create meeting
    const meeting = await prisma.meeting.create({
      data: {
        startDate,
        endDate,
        meetingRoomId: roomId || null,
        location: location || null,
        participants: {
          create: [
            {
              entityId: senderId,
              entityType: senderType,
              type: MeetingParticipantType.SENDER,
            },
            {
              entityId: receiverId,
              entityType: receiverType,
              type: MeetingParticipantType.RECEIVER,
            },
          ],
        },
        type: MeetingType.REQUEST,
        status: MeetingStatus.PENDING,
        createdById: senderId,
        eventId,
      },
      include: {
        meetingRoom: {
          include: {
            translations: true,
          },
        },
        participants: {
          include: {
            user: {
              include: {
                profiles: {
                  where: {
                    active: true,
                    eventId: eventId,
                  },
                  take: 1,
                },
              },
            },
            sponsor: {
              include: {
                translations: {
                  include: {
                    language: true,
                  },
                },
              },
            },
            exhibitor: {
              include: {
                translations: {
                  include: {
                    language: true,
                  },
                },
              },
            },
          },
        },
      },
    });
    const expoPushTokens = await getDeviceTokensByUser(
      meeting.participants.find(
        (p) => p.type === MeetingParticipantType.RECEIVER
      )?.entityId as string,
      meeting.eventId
    );

    const senderName =
      meeting?.participants.find(
        (p) => p.type === MeetingParticipantType.SENDER
      )?.user?.profiles?.[0]?.name ||
      meeting?.participants.find(
        (p) => p.type === MeetingParticipantType.SENDER
      )?.user?.name ||
      meeting?.participants
        .find((p) => p.type === MeetingParticipantType.SENDER)
        ?.sponsor?.translations?.find((t) => t.language?.code === lang)?.name ||
      meeting?.participants.find(
        (p) => p.type === MeetingParticipantType.SENDER
      )?.sponsor?.translations?.[0]?.name ||
      meeting?.participants
        .find((p) => p.type === MeetingParticipantType.SENDER)
        ?.exhibitor?.translations?.find((t) => t.language?.code === lang)
        ?.name ||
      meeting?.participants.find(
        (p) => p.type === MeetingParticipantType.SENDER
      )?.exhibitor?.translations?.[0]?.name;

    // The meeting is already committed. Push is best-effort so provider
    // contention from a broadcast cannot turn a successful booking into 500.
    void sendExpoNotification(
      meeting.participants.find(
        (p) => p.type === MeetingParticipantType.RECEIVER
      )?.entityId as string,
      expoPushTokens,
      `Have Meeting Request From ${senderName}`,
      {
        type: "meeting_request",
      }
    ).catch(() => undefined);
    return meeting;
  } catch (error: any) {
    console.error(error);
    throw new Error(error.message);
  }
};

async function canActAsMeetingEntity(
  actorUserId: string,
  entity: { entityId: string; entityType: ParticipantType },
  eventId: string
): Promise<boolean> {
  if (entity.entityId === actorUserId) return true;

  if (
    entity.entityType !== ParticipantType.EXHIBITOR &&
    entity.entityType !== ParticipantType.SPONSOR
  ) {
    return false;
  }

  const user = await prisma.user.findUnique({
    where: { id: actorUserId },
    select: { email: true },
  });

  const byTeam =
    entity.entityType === ParticipantType.EXHIBITOR
      ? await prisma.exhibitor.findFirst({
          where: {
            id: entity.entityId,
            eventId,
            teamIds: { has: actorUserId },
          },
          select: { id: true },
        })
      : await prisma.sponsor.findFirst({
          where: {
            id: entity.entityId,
            eventId,
            teamIds: { has: actorUserId },
          },
          select: { id: true },
        });
  if (byTeam) return true;

  // Mongo email equality is case-sensitive.
  if (!user?.email) return false;
  const company =
    entity.entityType === ParticipantType.EXHIBITOR
      ? await prisma.exhibitor.findFirst({
          where: { id: entity.entityId, eventId },
          select: { id: true, email: true },
        })
      : await prisma.sponsor.findFirst({
          where: { id: entity.entityId, eventId },
          select: { id: true, email: true },
        });
  return (
    !!company?.email && company.email.toLowerCase() === user.email.toLowerCase()
  );
}

async function assertCanRespondToMeeting(
  actorUserId: string,
  receiver: { entityId: string; entityType: ParticipantType },
  eventId: string,
  sender?: { entityId: string; entityType: ParticipantType }
) {
  if (!(await canActAsMeetingEntity(actorUserId, receiver, eventId))) {
    throw new Error("Forbidden");
  }
  // Block only personally originated requests (not multi-company membership).
  if (sender?.entityId === actorUserId) {
    throw new Error("Forbidden");
  }
}

export const updateMeetingRequestStatus = async ({
  meetingId,
  action,
  notificationId,
  actorUserId,
  eventId,
  lang = "en",
}: {
  meetingId: string;
  action: MeetingStatus;
  notificationId?: string;
  /** Required — caller must be the RECEIVER (or company team/email). */
  actorUserId: string;
  /** When set, meeting must belong to this event (slug-scoped routes). */
  eventId?: string;
  lang?: string;
}) => {
  try {
    const meeting = await prisma.meeting.findUnique({
      where: { id: meetingId },
      include: {
        participants: true,
      },
    });

    if (!meeting) {
      throw new Error("Meeting not found.");
    }

    if (eventId && meeting.eventId !== eventId) {
      throw new Error("Forbidden");
    }

    if (meeting.status !== MeetingStatus.PENDING) {
      throw new Error("Only pending meetings can be updated.");
    }

    const receiver = meeting.participants.find(
      (p) => p.type === MeetingParticipantType.RECEIVER
    );
    if (!receiver) {
      throw new Error("Meeting receiver not found.");
    }

    const sender = meeting.participants.find(
      (p) => p.type === MeetingParticipantType.SENDER
    );

    await assertCanRespondToMeeting(
      actorUserId,
      receiver,
      meeting.eventId,
      sender
        ? { entityId: sender.entityId, entityType: sender.entityType }
        : undefined
    );

    const updatedMeeting = await prisma.meeting.update({
      where: { id: meetingId },
      data: {
        status: action,
      },
      include: {
        meetingRoom: {
          include: {
            translations: true,
          },
        },
        participants: {
          include: {
            user: {
              include: {
                profiles: {
                  where: {
                    active: true,
                    eventId: meeting.eventId,
                  },
                  take: 1,
                },
              },
            },
            sponsor: {
              include: {
                translations: {
                  include: {
                    language: true,
                  },
                },
              },
            },
            exhibitor: {
              include: {
                translations: {
                  include: {
                    language: true,
                  },
                },
              },
            },
          },
        },
      },
    });

    const ownedNotification = notificationId
      ? await prisma.notification.findFirst({
          where: {
            id: notificationId,
            entityId: meeting.id,
            type: NotificationsType.SEND_MEETING_REQUEST,
          },
          select: { id: true },
        })
      : null;

    const resolvedNotificationId =
      ownedNotification?.id ||
      (
        await prisma.notification.findFirst({
          where: {
            entityId: meeting.id,
            type: NotificationsType.SEND_MEETING_REQUEST,
          },
          select: { id: true },
        })
      )?.id;

    if (resolvedNotificationId) {
      await updateNotification(resolvedNotificationId, {
        type:
          action === MeetingStatus.ACCEPTED
            ? NotificationsType.APPROVED_MEETING_REQUEST
            : action === MeetingStatus.REJECTED
            ? NotificationsType.REJECTED_MEETING_REQUEST
            : NotificationsType.SEND_MEETING_REQUEST,
        readAt: new Date(),
        // Refresh relative time in the bell — content changed to accept/reject.
        sentAt: new Date(),
      });
    }
    await createNotification({
      type:
        action === MeetingStatus.ACCEPTED
          ? NotificationsType.APPROVED_MEETING_REQUEST
          : action === MeetingStatus.REJECTED
          ? NotificationsType.REJECTED_MEETING_REQUEST
          : NotificationsType.SEND_MEETING_REQUEST,
      entity: NotificationEntities.MEETING,
      entityId: meeting.id,
      senderId: meeting.participants.find(
        (p) => p.type === MeetingParticipantType.RECEIVER
      )?.entityId as string,
      receiverId: meeting.participants.find(
        (p) => p.type === MeetingParticipantType.SENDER
      )?.entityId as string,
      sender: meeting.participants.find(
        (p) => p.type === MeetingParticipantType.RECEIVER
      )?.entityType as ParticipantType,
      receiver: meeting.participants.find(
        (p) => p.type === MeetingParticipantType.SENDER
      )?.entityType as ParticipantType,
      eventId: meeting.eventId,
    });
    const expoPushTokens = await getDeviceTokensByUser(
      meeting.participants.find((p) => p.type === MeetingParticipantType.SENDER)
        ?.entityId as string,
      meeting.eventId
    );
    const senderName =
      updatedMeeting?.participants.find(
        (p) => p.type === MeetingParticipantType.RECEIVER
      )?.user?.profiles?.[0]?.name ||
      updatedMeeting?.participants.find(
        (p) => p.type === MeetingParticipantType.RECEIVER
      )?.user?.name ||
      updatedMeeting?.participants
        .find((p) => p.type === MeetingParticipantType.RECEIVER)
        ?.sponsor?.translations?.find((t) => t.language?.code === lang)?.name ||
      updatedMeeting?.participants.find(
        (p) => p.type === MeetingParticipantType.RECEIVER
      )?.sponsor?.translations?.[0]?.name ||
      updatedMeeting?.participants
        .find((p) => p.type === MeetingParticipantType.RECEIVER)
        ?.exhibitor?.translations?.find((t) => t.language?.code === lang)
        ?.name ||
      updatedMeeting?.participants.find(
        (p) => p.type === MeetingParticipantType.RECEIVER
      )?.exhibitor?.translations?.[0]?.name;

    await sendExpoNotification(
      meeting.participants.find(
        (p) => p.type === MeetingParticipantType.RECEIVER
      )?.entityId as string,
      expoPushTokens,
      `Meeting Request ${action.toLocaleLowerCase()} From ${senderName}`,
      {
        type: "meeting_request",
      }
    );
    return updatedMeeting;
  } catch (error: any) {
    console.error(error);
    throw new Error(error.message);
  }
};
